import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getLostItemCase, lostItemStatusBadge,
  driverRespondToLostItem, driverConfirmReturnFee, driverMarkReturned,
  getDisputeConfig,
} from '../../services/disputeService';
import PhotoUploader from '../../components/PhotoUploader';
import { ACCEPTED_IMAGE_TYPES } from '../../services/storageService';
import toast from 'react-hot-toast';

export default function LostItemDetailDriver() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Response form state (for status === 'reported')
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [response, setResponse] = useState('');
  const [responseNotes, setResponseNotes] = useState('');
  const [responsePhotos, setResponsePhotos] = useState([]);

  // Mark-delivered state
  const [showDeliveredForm, setShowDeliveredForm] = useState(false);
  const [deliveredNotes, setDeliveredNotes] = useState('');

  const load = () => {
    setLoading(true);
    getLostItemCase(caseId)
      .then(setCaseData)
      .catch((err) => toast.error(err.message || 'Could not load case'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [caseId]);
  useEffect(() => { getDisputeConfig().then(setConfig).catch(() => {}); }, []);

  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fmtDateOnly = (ts) => {
    if (!ts) return '';
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const daysUntil = (ts) => {
    if (!ts) return null;
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    if (isNaN(d.getTime())) return null;
    const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
    return days;
  };

  const handleSubmitResponse = async () => {
    if (!response) { toast.error('Select a response'); return; }
    setActionInProgress(true);
    try {
      await driverRespondToLostItem({
        caseId,
        response,
        notes: responseNotes || null,
        stagedPhotoUrls: responsePhotos.map(p => p.url),
      });
      toast.success('Response submitted');
      setShowResponseForm(false);
      setResponse('');
      setResponseNotes('');
      setResponsePhotos([]);
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleAcceptFee = async () => {
    if (!window.confirm(`Accept $${caseData.returnFeeOffered?.toFixed(2)} for returning this item? Rider will be charged immediately and you'll have 72 hours to deliver.`)) return;
    setActionInProgress(true);
    try {
      await driverConfirmReturnFee({ caseId, accept: true });
      toast.success('Fee accepted. Rider has been charged. Deliver within 72 hours.');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleDeclineFee = async () => {
    if (!window.confirm('Decline the offered fee? The case will move to pickup coordination — the rider will need to collect the item.')) return;
    setActionInProgress(true);
    try {
      await driverConfirmReturnFee({ caseId, accept: false });
      toast.success('Fee declined. Case moved to pickup coordination.');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  const handleMarkDelivered = async () => {
    setActionInProgress(true);
    try {
      await driverMarkReturned({ caseId, notes: deliveredNotes || null });
      toast.success('Marked as delivered. Rider has 7 days to confirm.');
      setShowDeliveredForm(false);
      setDeliveredNotes('');
      load();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
    setActionInProgress(false);
  };

  if (loading) return <div className="p-10 text-center text-muted">Loading...</div>;
  if (!caseData) return <div className="p-10 text-center text-muted">Case not found</div>;

  const badge = lostItemStatusBadge(caseData.status);
  const isTerminal = ['resolved_returned', 'resolved_collected', 'unresolved',
    'expired_driver_custody', 'unresolved_pickup_timeout', 'cancelled_by_rider', 'admin_resolved'].includes(caseData.status);
  const custodyDaysLeft = daysUntil(caseData.custodyDeadline);
  const deliveryDaysLeft = daysUntil(caseData.returnDeliveryDeadline);

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/driver/lost-items')} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← All lost items</button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h1 className="font-serif m-0">{caseData.referenceNumber}</h1>
          <div className="text-[0.78rem] text-muted mt-1">
            Reported by {caseData.riderName} on {fmtDate(caseData.reportedAt || caseData.createdAt)}
          </div>
        </div>
        <span className="px-3 py-1 rounded-full text-[0.78rem] font-bold" style={{ background: badge.bg, color: badge.color }}>
          {badge.label}
        </span>
      </div>

      {/* Custody countdown banner */}
      {!isTerminal && caseData.custodyDeadline && custodyDaysLeft !== null && custodyDaysLeft >= 0 && (
        <div className="p-3 rounded-[10px] mb-4 text-[0.85rem]"
          style={{
            background: custodyDaysLeft <= 3 ? '#fef3c7' : '#f0fdfa',
            border: `1px solid ${custodyDaysLeft <= 3 ? '#fde68a' : '#99f6e4'}`,
            color: custodyDaysLeft <= 3 ? '#92400e' : '#0a8270',
          }}>
          {custodyDaysLeft === 0
            ? '⏱ Custody period expires today. After today, you may dispose of the item at your discretion.'
            : custodyDaysLeft <= 3
              ? `⏱ Custody period expires in ${custodyDaysLeft} day${custodyDaysLeft === 1 ? '' : 's'} (${fmtDateOnly(caseData.custodyDeadline)}). After expiry, you may dispose of the item.`
              : `Custody period: ${custodyDaysLeft} days remaining (until ${fmtDateOnly(caseData.custodyDeadline)})`}
        </div>
      )}

      {/* Item details (from rider) */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-4">
        <div className="text-[0.78rem] text-muted font-semibold uppercase mb-1.5">
          Item description (from rider)
        </div>
        <div className="text-[0.95rem] text-brand-dark mb-2">{caseData.description}</div>
        <div className="text-[0.82rem] text-body">
          Reported location in vehicle: <strong>{caseData.locationInVehicle?.replace(/_/g, ' ')}</strong>
        </div>
        <div className="text-[0.82rem] text-body mt-1">
          From trip: <strong>{caseData.bookingReferenceNumber || caseData.bookingId?.substring(0, 8)}</strong>
        </div>

        {caseData.reportPhotos?.length > 0 && (
          <div className="mt-3">
            <div className="text-[0.78rem] text-muted mb-1">Photos from rider:</div>
            <div className="flex flex-wrap gap-1.5">
              {caseData.reportPhotos.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                  <img src={url} alt={`Reported ${i + 1}`}
                    className="w-[70px] h-[70px] object-cover rounded-lg border border-slate-200" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── STATE-DRIVEN DRIVER ACTIONS ─── */}

      {/* State: reported — driver must respond */}
      {caseData.status === 'reported' && (
        <div className="p-[18px] rounded-2xl bg-white mb-4" style={{ border: '2px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2 text-brand-dark">Please respond within 48 hours</h3>
          <p className="text-body text-[0.85rem] mb-3.5">
            Check your vehicle thoroughly. Did you find the item the rider described?
          </p>

          {!showResponseForm ? (
            <button onClick={() => setShowResponseForm(true)} className="w-full p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">Respond now</button>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">What did you find? *</label>
                <div className="grid gap-2">
                  {[
                    {
                      value: 'found_will_return',
                      title: '✓ I have the item — willing to return it',
                      desc: 'Rider will offer you a return fee (minimum $30). You can accept or decline.',
                    },
                    {
                      value: 'found_needs_pickup',
                      title: '✓ I have the item — but rider must collect',
                      desc: 'Rider arranges to collect from a location you agree on. No return fee.',
                    },
                    {
                      value: 'not_found',
                      title: '✗ I checked but did not find the item',
                      desc: 'Admin will investigate. The rider will be notified.',
                    },
                  ].map(opt => (
                    <label key={opt.value} className="block p-3 rounded-[10px] cursor-pointer"
                      style={{
                        border: `1.5px solid ${response === opt.value ? '#2dd4a8' : '#e2e8f0'}`,
                        background: response === opt.value ? 'rgba(45,212,168,0.05)' : '#fff',
                      }}>
                      <input type="radio" name="response" value={opt.value}
                        checked={response === opt.value}
                        onChange={(e) => setResponse(e.target.value)}
                        className="mr-2" />
                      <strong className="text-[0.9rem] text-brand-dark">{opt.title}</strong>
                      <div className="text-[0.78rem] text-body mt-0.5 ml-[22px]">
                        {opt.desc}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea className="form-input" rows={2}
                  placeholder="e.g. 'Found in back seat pocket' or 'Searched thoroughly, not in vehicle'"
                  value={responseNotes} onChange={(e) => setResponseNotes(e.target.value)} />
              </div>

              {(response === 'found_will_return' || response === 'found_needs_pickup') && (
                <PhotoUploader
                  userId={userProfile?.id}
                  value={responsePhotos}
                  onChange={setResponsePhotos}
                  maxFiles={3}
                  acceptedTypes={ACCEPTED_IMAGE_TYPES}
                  label="Confirmation photos (optional)"
                  helperText="Optional photos confirming you have the item. Up to 3 images."
                />
              )}

              <div className="flex gap-2 mt-3">
                <button onClick={() => { setShowResponseForm(false); setResponse(''); }} className="flex-1 p-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Cancel</button>
                <button onClick={handleSubmitResponse} disabled={actionInProgress || !response} className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold"
                  style={{ opacity: (actionInProgress || !response) ? 0.6 : 1 }}>{actionInProgress ? 'Submitting...' : 'Submit response'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* State: driver_found_will_return — waiting for rider to offer fee */}
      {caseData.status === 'driver_found_will_return' && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          Waiting for rider to offer a return fee. You'll be notified when they do, and you can accept or decline.
        </div>
      )}

      {/* State: awaiting_driver_fee_confirmation — driver must accept/decline */}
      {caseData.status === 'awaiting_driver_fee_confirmation' && (
        <div className="p-[18px] rounded-2xl bg-white mb-4" style={{ border: '2px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2 text-brand-dark">Rider offered ${caseData.returnFeeOffered?.toFixed(2)}</h3>
          <p className="text-body text-[0.85rem] mb-3.5">
            Accept to be authorized to deliver the item. The rider's card will be charged immediately
            and you'll have 72 hours to deliver. You receive 100% of this amount on rider confirmation.
            <br /><br />
            Decline to move the case to pickup coordination — the rider will need to collect the item from you.
          </p>
          <div className="flex gap-2">
            <button onClick={handleAcceptFee} disabled={actionInProgress} className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">{actionInProgress ? '...' : `✓ Accept $${caseData.returnFeeOffered?.toFixed(2)}`}</button>
            <button onClick={handleDeclineFee} disabled={actionInProgress} className="flex-1 p-3 rounded-[10px] bg-white cursor-pointer font-semibold"
              style={{ border: '1.5px solid #ff6b6b', color: '#ff6b6b' }}>Decline</button>
          </div>
        </div>
      )}

      {/* State: return_paid — driver must deliver, then mark delivered */}
      {caseData.status === 'return_paid' && (
        <div className="p-[18px] rounded-2xl bg-white mb-4" style={{ border: '2px solid #2dd4a8' }}>
          <h3 className="mt-0 mb-2 text-brand-dark">You're authorized to deliver</h3>
          <div className="mb-3 p-2.5 rounded-lg text-[0.85rem]" style={{ background: '#f0fdfa', color: '#0a8270' }}>
            Rider paid <strong>${caseData.returnFeeOffered?.toFixed(2)}</strong>.
            {deliveryDaysLeft !== null && (
              <> Delivery deadline: <strong>{deliveryDaysLeft} day{deliveryDaysLeft === 1 ? '' : 's'}</strong> ({fmtDateOnly(caseData.returnDeliveryDeadline)})</>
            )}
          </div>
          <p className="text-body text-[0.85rem] mb-3.5">
            Deliver the item to the rider. Contact them through the in-app chat on the original trip
            to arrange a convenient time and place. Once delivered, mark it here — the rider then has
            7 days to confirm receipt, after which you're paid out.
          </p>

          {!showDeliveredForm ? (
            <div className="flex gap-2">
              <button onClick={() => caseData.bookingId && navigate(`/driver/trip/${caseData.bookingId}`)} className="flex-1 p-3 rounded-[10px] border-[1.5px] border-brand-primary bg-white text-brand-primary cursor-pointer font-semibold">💬 Message rider</button>
              <button onClick={() => setShowDeliveredForm(true)} className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">✓ Mark as delivered</button>
            </div>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">Delivery notes (optional)</label>
                <textarea className="form-input" rows={2}
                  placeholder="e.g. 'Delivered to rider at home address'"
                  value={deliveredNotes} onChange={(e) => setDeliveredNotes(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowDeliveredForm(false); setDeliveredNotes(''); }} className="flex-1 p-3 rounded-[10px] border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold">Cancel</button>
                <button onClick={handleMarkDelivered} disabled={actionInProgress} className="flex-1 p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold">{actionInProgress ? '...' : 'Confirm delivered'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* State: return_delivered_by_driver — waiting for rider confirmation */}
      {caseData.status === 'return_delivered_by_driver' && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          You've marked the item as delivered. Waiting for rider to confirm receipt. If they don't respond by{' '}
          {fmtDateOnly(caseData.riderConfirmDeadline)}, you'll be paid out automatically.
        </div>
      )}

      {/* State: driver_found_needs_pickup / awaiting_pickup_coordination */}
      {(caseData.status === 'driver_found_needs_pickup' || caseData.status === 'awaiting_pickup_coordination') && (
        <div className="p-[18px] rounded-2xl mb-4" style={{ background: '#dbeafe', border: '1px solid #bfdbfe' }}>
          <h3 className="mt-0 mb-2" style={{ color: '#1e40af' }}>Coordinate pickup with rider</h3>
          <p className="text-[0.85rem] mb-3" style={{ color: '#1e40af' }}>
            Use in-app messages on the original trip to agree on a pickup location and time. The rider
            will book a delivery trip to your agreed location. Remember: you have until{' '}
            <strong>{fmtDateOnly(caseData.custodyDeadline)}</strong> ({custodyDaysLeft} day{custodyDaysLeft === 1 ? '' : 's'}) before custody expires.
          </p>
          {caseData.bookingId && (
            <button onClick={() => navigate(`/driver/trip/${caseData.bookingId}`)} className="px-3.5 py-2 rounded-lg bg-white cursor-pointer font-semibold text-[0.85rem]"
              style={{ border: '1.5px solid #1e40af', color: '#1e40af' }}>💬 Open trip messages</button>
          )}
        </div>
      )}

      {/* State: driver_not_found — admin investigating */}
      {caseData.status === 'driver_not_found' && (
        <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 mb-4 text-[0.9rem] text-body">
          You reported that the item was not found. Reservo admin has been notified and will follow up if needed.
        </div>
      )}

      {/* State: admin_review_refund */}
      {caseData.status === 'admin_review_refund' && (
        <div className="p-3.5 rounded-xl mb-4 text-[0.9rem]" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
          The rider has requested a refund. Reservo admin is reviewing the case. No action needed from you for now.
        </div>
      )}

      {/* Terminal states */}
      {isTerminal && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 mb-4">
          <div className="text-[0.85rem] text-body font-semibold mb-1">
            Case resolved
          </div>
          <div className="text-[0.85rem] text-body">
            <strong>Outcome:</strong> {caseData.resolutionType || caseData.status?.replace(/_/g, ' ')}
          </div>
          {caseData.resolutionNotes && (
            <div className="text-[0.82rem] text-body mt-1.5 italic">
              "{caseData.resolutionNotes}"
            </div>
          )}
          {caseData.resolvedAt && (
            <div className="text-[0.78rem] text-muted mt-1.5">
              Resolved {fmtDate(caseData.resolvedAt)}
            </div>
          )}
          {caseData.payoutAt && caseData.returnFeeOffered && (
            <div className="mt-2.5 p-2 rounded-lg text-[0.82rem] font-semibold" style={{ background: 'rgba(45,212,168,0.1)', color: '#0a8270' }}>
              ✓ Paid out ${caseData.returnFeeOffered.toFixed(2)} on {fmtDateOnly(caseData.payoutAt)}
            </div>
          )}
        </div>
      )}

      {/* Status history (collapsible) */}
      {caseData.statusHistory && caseData.statusHistory.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-[0.85rem] text-body font-semibold">
            Case history ({caseData.statusHistory.length} events)
          </summary>
          <div className="mt-2 p-3 rounded-[10px] bg-slate-50 text-[0.78rem] text-body">
            {caseData.statusHistory.map((h, i) => (
              <div key={i} className="mb-2 pb-2" style={{ borderBottom: i < caseData.statusHistory.length - 1 ? '1px solid #e2e8f0' : 'none' }}>
                <div>
                  <strong>{h.from || '—'}</strong> → <strong>{h.to}</strong>
                </div>
                {h.note && <div className="italic mt-0.5">"{h.note}"</div>}
                <div className="text-muted mt-0.5">{fmtDate(h.at)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
      <BackButtonBottom to="/driver/lost-items" label="All lost items" />
    </div>
  );
}